/**
 * PremiumNotice — how an entitlement denial (HTTP 402) looks, everywhere.
 *
 * One component for every 402 in the app, so a Premium limit always reads and
 * looks the same whether it was hit by saving a recipe, inviting someone or
 * starting a paid import. Rendered INLINE, right where the action was taken
 * (next to the save pill, under the invite form, under the import button) —
 * never as a modal or a toast: the user pressed a specific button and the
 * answer belongs at that button.
 *
 * It is NOT styled as an error. A 402 is not a mistake — the request was
 * well-formed and the user is allowed to make it; the plan simply doesn't
 * include it. Hence the calm honey/clay-tinted card rather than
 * `.image-error`'s red-ish prose, and `role="status"` (polite) rather than
 * `role="alert"`.
 */

import React from "react";

import { entitlementMessage, type EntitlementDenial } from "../lib/entitlement";
import { UpgradeCta } from "./UpgradeCta";

export function PremiumNotice({
  denial,
  testId = "premium-notice",
}: {
  /** The parsed 402 — an `EntitlementRequiredError` satisfies this structurally. */
  denial: EntitlementDenial;
  /**
   * Per-call-site test id, so a page with several possible denials (e.g. the
   * editor) stays addressable. The message node gets `${testId}-message` and
   * the CTA seam `${testId}-cta`.
   */
  testId?: string;
}): React.JSX.Element {
  return (
    <div className="premium-notice" data-testid={testId} role="status">
      <p className="premium-notice-msg" data-testid={`${testId}-message`}>
        {entitlementMessage(denial)}
      </p>
      <UpgradeCta testId={`${testId}-cta`} />
    </div>
  );
}
